// Emir defteri satırlarının kurulumu: gerçek derinlik + kendi gölge emirlerimiz.
//
// **Gölge miktarımız gerçek miktara EKLENMEZ.** Sebep `K.Miktar` sütununun
// anlamı: "bu fiyata kadar süpürürsem toplam ne alırım". Kendi emrimizle işlem
// yapamayacağımıza göre onu bu toplama katmak yanıltıcı olur — eşleşme
// listesinde gölge dolumları ayrı sütunda tutmamızla aynı gerekçe.
//
// Emrimiz gerçek bir seviyeye denk gelmiyorsa (tipik durum: makasın içine fiyat
// iyileştirmesi) o fiyat için **sentetik satır** açılır: miktar sütunları boş,
// yalnız kendi işaretimiz görünür.

import type { Depth, Level, ShadowOrder } from './types'

export interface LadderRow {
  price: number
  /** Piyasadaki gerçek miktar. Sentetik satırda 0. */
  quantity: number
  /**
   * Süpürülebilir kalan lot (görünen − tükettiğimiz). 0 ve gerçek miktar > 0
   * ise seviye "süpürüldü": tahtada durur ama bize kapalıdır — işaretlenir,
   * tıklanamaz. Hızlı Al/Sat miktar önizlemesi de bunun kümülatifidir.
   */
  available: number
  /** Bu seviyedeki kendi açık gölge miktarımız (lot). 0 ise emrimiz yok. */
  mine: number
  /** Gerçek miktarlar üzerinden kümülatif — kendi emrimiz dahil DEĞİL. */
  cumulative: number
  /** Piyasada bu fiyatta seviye yok, satır yalnız bizim emrimiz için açıldı. */
  synthetic: boolean
  /**
   * Bu fiyat/yöndeki kendi açık emirlerimiz. Genelde tek eleman; aynı fiyattan
   * birden çok emir açmak mümkün olduğu için dizi. Defterdeki düzenle/sil
   * ikonları buna bakıyor.
   */
  orders: ShadowOrder[]
}

export interface Ladder {
  bids: LadderRow[]
  asks: LadderRow[]
  /** Ortak çubuk ölçeği (iki tarafın toplam gerçek derinliğinden büyüğü). */
  scale: number
  rowCount: number
}

/** Fiyat eşlemesi için anahtar. EPİAŞ tik adımı 0,1 — iki hane fazlasıyla yeter. */
function anahtar(price: number): string {
  return price.toFixed(2)
}

/** Açık gölge emirlerimiz, fiyata göre gruplanmış (yön başına). */
function kendiEmirlerimiz(
  orders: ShadowOrder[],
  side: 'BUY' | 'SELL',
): Map<string, ShadowOrder[]> {
  const map = new Map<string, ShadowOrder[]>()
  for (const order of orders) {
    if (order.status !== 'OPEN' || order.side !== side) continue
    // Dolan kısım tahtada durmuyor; görünmesi gereken kalan miktar.
    if (order.remaining <= 0) continue
    const k = anahtar(order.price)
    const grup = map.get(k)
    if (grup) grup.push(order)
    else map.set(k, [order])
  }
  return map
}

const toplamKalan = (orders: ShadowOrder[]) =>
  orders.reduce((sum, o) => sum + o.remaining, 0)

function tarafKur(
  levels: Level[],
  orders: ShadowOrder[],
  side: 'BUY' | 'SELL',
): LadderRow[] {
  const bizim = kendiEmirlerimiz(orders, side)

  const rows: LadderRow[] = levels.map((level) => {
    const k = anahtar(level.price)
    const kendi = bizim.get(k) ?? []
    bizim.delete(k)                 // eşleşti, sentetik satır gerekmiyor
    return {
      price: level.price,
      quantity: level.quantity,
      // Eski sunucu yükünde alan yoksa tamamı açık sayılır.
      available: level.available ?? level.quantity,
      mine: toplamKalan(kendi),
      cumulative: 0,
      synthetic: false,
      orders: kendi,
    }
  })

  // Gerçek seviyesi olmayan kendi fiyatlarımız — makasın içindeki emirlerimiz
  // burada görünür hâle geliyor.
  for (const [k, kendi] of bizim) {
    rows.push({
      price: Number(k),
      quantity: 0,
      available: 0,
      mine: toplamKalan(kendi),
      cumulative: 0,
      synthetic: true,
      orders: kendi,
    })
  }

  // Alışta yüksek fiyat daha iyi, satışta düşük.
  rows.sort((a, b) => (side === 'BUY' ? b.price - a.price : a.price - b.price))

  // Kümülatif YALNIZ gerçek miktarlar üzerinden; sentetik satır toplamı
  // arttırmaz, bir öncekinin değerini taşır.
  let toplam = 0
  for (const row of rows) {
    toplam += row.quantity
    row.cumulative = toplam
  }
  return rows
}

export function buildLadder(depth: Depth | null, orders: ShadowOrder[]): Ladder {
  const bids = depth ? tarafKur(depth.bids, orders, 'BUY') : []
  const asks = depth ? tarafKur(depth.asks, orders, 'SELL') : []
  const scale = Math.max(
    bids[bids.length - 1]?.cumulative ?? 0,
    asks[asks.length - 1]?.cumulative ?? 0,
    1,
  )
  return { bids, asks, scale, rowCount: Math.max(bids.length, asks.length) }
}
